import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  liveChatApi, HEARTBEAT_MS, INBOX_POLL_MS, THREAD_POLL_MS,
  canTakeChats, mergeMessages, lastSeqOf, newlyWaiting, formatWait,
  systemLine, endedLine, readAvailable, writeAvailable,
  CASE_CATEGORIES, CASE_PRIORITIES, caseEdits,
} from '../lib/liveChat';

// The live-chat dock — bottom-right, on every CRM page.
//
// A shopper on squish.co asks the assistant for a person. If anyone here is
// "Available for chat", they land in this queue with the assistant
// conversation so far; whoever accepts talks to them inside the shopper's own
// chat window. Nobody accepts inside three minutes (or everyone goes Away) and
// the shopper is offered a callback instead — so Away is always safe.
//
// Availability is a heartbeat, not a flag: this tab tells the backend every
// 20s that you are still here. Close the tab and you stop receiving chats
// within a minute. The same "on shift" presence is what phone routing will use
// later, which is why it is sent as a channel list.
export default function LiveChatDock({ me, onOpenCase = null }) {
  const eligible = canTakeChats(me?.role);
  const [available, setAvailableRaw] = useState(readAvailable);
  const [open, setOpen] = useState(false);
  const [inbox, setInbox] = useState({ waiting: [], mine: [], othersActive: 0, agentsOnline: [] });
  const [offline, setOffline] = useState(null);      // why the dock cannot reach live chat
  const [selected, setSelected] = useState('queue');  // 'queue' | sessionId
  const [threads, setThreads] = useState({});         // sessionId -> thread
  const [readSeq, setReadSeq] = useState({});         // sessionId -> last seq seen
  const [notice, setNotice] = useState(null);
  const threadsRef = useRef(threads);
  threadsRef.current = threads;
  const seenWaitingRef = useRef([]);
  // Which sessions are mine, per the server. whoami carries no Salesforce id,
  // so ownership is never inferred client-side — the inbox's `mine` list and a
  // successful accept are the only two sources.
  const mineIdsRef = useRef(new Set());
  const baseTitleRef = useRef(document.title);

  const flash = useCallback((text) => {
    setNotice(text);
    setTimeout(() => setNotice((n) => (n === text ? null : n)), 5000);
  }, []);

  const setAvailable = useCallback((on) => {
    setAvailableRaw(on);
    writeAvailable(on);
    // Ask for desktop notifications on the click that turns chat on — a
    // browser only allows the prompt from a user gesture.
    if (on && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
    if (!on) liveChatApi.presence('away').catch(() => {});
  }, []);

  // ── Presence heartbeat ────────────────────────────────────────────────────
  // Runs while Available, AND while Away with a chat still open: the backend
  // ends a quiet chat whose staffer has stopped beating, so going Away to stop
  // NEW chats must not silently drop the shopper already talking to you.
  const hasMine = inbox.mine.length > 0;
  const beating = eligible && (available || hasMine);
  useEffect(() => {
    if (!beating) return undefined;
    let alive = true;
    const status = available ? 'available' : 'away';
    const beat = () => liveChatApi.presence(status)
      .then(() => { if (alive) setOffline(null); })
      .catch((err) => { if (alive) setOffline(err.status === 403 ? err.message : 'Live chat is unreachable — retrying'); });
    beat();
    const id = setInterval(beat, HEARTBEAT_MS);
    // Leaving the page is going Away: the queue should not wait a minute for
    // a heartbeat that will never come.
    const onHide = () => { if (available) liveChatApi.awayOnUnload(); };
    window.addEventListener('pagehide', onHide);
    return () => { alive = false; clearInterval(id); window.removeEventListener('pagehide', onHide); };
  }, [beating, available]);

  // ── Inbox poll ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!eligible || (!available && !hasMine)) return undefined;
    let alive = true;
    const poll = async () => {
      try {
        const next = await liveChatApi.inbox();
        if (!alive) return;
        setOffline(null);
        const fresh = newlyWaiting(seenWaitingRef.current, next.waiting);
        seenWaitingRef.current = next.waiting.map((w) => w.sessionId);
        if (fresh.length && available) ping(fresh[0]);
        mineIdsRef.current = new Set(next.mine.map((m) => m.sessionId));
        setInbox(next);
      } catch (err) {
        if (alive) setOffline(err.status === 403 ? err.message : 'Live chat is unreachable — retrying');
      }
    };
    poll();
    const id = setInterval(poll, INBOX_POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [eligible, available, hasMine]);

  // Tab title carries the queue while the CRM is in the background.
  useEffect(() => {
    const n = inbox.waiting.length;
    document.title = n > 0 && available ? `(${n}) Chat waiting · ${baseTitleRef.current}` : baseTitleRef.current;
  }, [inbox.waiting.length, available]);

  // ── Thread polls: every chat I hold, plus the one I am previewing ─────────
  const pollIds = [...new Set([
    ...inbox.mine.map((m) => m.sessionId),
    ...Object.values(threads).filter((t) => t.status === 'active' && t.mineFlag).map((t) => t.sessionId),
    ...(selected !== 'queue' && threads[selected]?.status === 'waiting' ? [selected] : []),
  ])].sort().join(',');

  const refreshThread = useCallback(async (id) => {
    const cur = threadsRef.current[id];
    const after = cur ? lastSeqOf(cur.messages) : 0;
    const view = await liveChatApi.session(id, after);
    setThreads((prev) => {
      const old = prev[id] || { messages: [] };
      return {
        ...prev,
        [id]: {
          ...old,
          ...view,
          context: view.context !== undefined ? view.context : old.context,
          messages: mergeMessages(old.messages, view.messages),
          mineFlag: old.mineFlag || mineIdsRef.current.has(id),
        },
      };
    });
  }, []);

  useEffect(() => {
    if (!pollIds) return undefined;
    const ids = pollIds.split(',');
    const tick = () => ids.forEach((id) => refreshThread(id).catch(() => { /* next tick */ }));
    tick();
    const iv = setInterval(tick, THREAD_POLL_MS);
    return () => clearInterval(iv);
  }, [pollIds, refreshThread]);

  // Opening a thread marks it read.
  useEffect(() => {
    if (!open || selected === 'queue') return;
    const t = threads[selected];
    if (t) setReadSeq((r) => (r[selected] === lastSeqOf(t.messages) ? r : { ...r, [selected]: lastSeqOf(t.messages) }));
  }, [open, selected, threads]);

  function ping(session) {
    beep();
    // A panel left on a finished thread would hide the new arrival; one on a
    // live conversation stays put — the badge and chime do the telling.
    setSelected((cur) => {
      const t = threadsRef.current[cur];
      return t && ['waiting', 'active'].includes(t.status) ? cur : 'queue';
    });
    if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        const n = new Notification('Someone wants a person', {
          body: `${session.customerLabel}${session.page ? ` · ${session.page}` : ''}\n${session.preview || ''}`.slice(0, 180),
          tag: `live-chat-${session.sessionId}`,
        });
        n.onclick = () => { window.focus(); setOpen(true); setSelected('queue'); n.close(); };
      } catch { /* notifications are a nicety */ }
    }
  }

  async function preview(id) {
    setSelected(id);
    if (!threadsRef.current[id]) {
      try { await refreshThread(id); } catch (err) { flash(err.message); }
    }
  }

  async function accept(id) {
    try {
      const view = await liveChatApi.accept(id);
      mineIdsRef.current.add(id);
      setThreads((prev) => ({ ...prev, [id]: { ...view, mineFlag: true } }));
      setSelected(id);
      setInbox((ib) => ({
        ...ib,
        waiting: ib.waiting.filter((w) => w.sessionId !== id),
        mine: [...ib.mine.filter((m) => m.sessionId !== id), { ...view }],
      }));
    } catch (err) {
      flash(err.code === 'taken' || err.code === 'closed' ? err.message : `Could not accept: ${err.message}`);
      setInbox((ib) => ({ ...ib, waiting: ib.waiting.filter((w) => w.sessionId !== id) }));
      if (err.code === 'closed') setSelected('queue');
    }
  }

  async function send(id, text) {
    const msg = await liveChatApi.send(id, text);
    setThreads((prev) => ({ ...prev, [id]: { ...prev[id], messages: mergeMessages(prev[id]?.messages, [msg]) } }));
  }

  async function end(id) {
    try {
      await liveChatApi.end(id);
      await refreshThread(id).catch(() => {});
      setInbox((ib) => ({ ...ib, mine: ib.mine.filter((m) => m.sessionId !== id), waiting: ib.waiting.filter((w) => w.sessionId !== id) }));
    } catch (err) {
      flash(err.message);
    }
  }

  // The chat's Case: the staffer corrects what it is about. The server's
  // answer is what the thread shows, so a failed save cannot look saved.
  async function saveCase(id, fields) {
    const saved = await liveChatApi.editCase(id, fields);
    setThreads((prev) => ({ ...prev, [id]: { ...prev[id], case: { ...(prev[id]?.case || {}), ...saved } } }));
    return saved;
  }

  function dismiss(id) {
    setThreads((prev) => { const next = { ...prev }; delete next[id]; return next; });
    setSelected('queue');
  }

  if (!eligible) return null;

  const myThreads = Object.values(threads).filter((t) => t.mineFlag);
  const unreadFor = (t) => (t.messages || []).filter((m) => m.from === 'customer' && m.seq > (readSeq[t.sessionId] || 0)).length;
  const totalUnread = myThreads.reduce((n, t) => n + (open && selected === t.sessionId ? 0 : unreadFor(t)), 0);
  const waitingCount = available ? inbox.waiting.length : 0;
  const selectedThread = selected !== 'queue' ? threads[selected] : null;

  return (
    <div style={S.root}>
      {open && (
        <div style={S.panel} role="dialog" aria-label="Live chat">
          <div style={S.header}>
            <button
              onClick={() => setAvailable(!available)}
              style={{ ...S.toggle, borderColor: available ? 'var(--accent3)' : 'var(--border)' }}
              title={available ? 'You will get new chats. Click to go Away.' : 'Click to start taking chats.'}
            >
              <span style={{ ...S.dot, background: available ? 'var(--accent3)' : 'var(--text3)' }} />
              {available ? 'Available for chat' : 'Away'}
            </button>
            <span style={S.meta}>
              {inbox.agentsOnline.length > 0 ? `${inbox.agentsOnline.length} on` : ''}
              {inbox.othersActive > 0 ? ` · ${inbox.othersActive} with others` : ''}
            </span>
            <button onClick={() => setOpen(false)} style={S.iconBtn} aria-label="Minimise">▾</button>
          </div>

          <div style={S.tabs}>
            <Tab active={selected === 'queue'} onClick={() => setSelected('queue')}>
              Queue{waitingCount ? ` (${waitingCount})` : ''}
            </Tab>
            {myThreads.map((t) => (
              <Tab key={t.sessionId} active={selected === t.sessionId} onClick={() => setSelected(t.sessionId)}
                dim={t.status !== 'active'} badge={selected === t.sessionId ? 0 : unreadFor(t)}>
                {t.customerLabel || 'Visitor'}
              </Tab>
            ))}
          </div>

          {offline && <div style={S.banner}>{offline}</div>}
          {notice && <div style={{ ...S.banner, background: 'var(--bg3)', color: 'var(--text)' }}>{notice}</div>}

          {selected === 'queue' || !selectedThread ? (
            <Queue
              available={available}
              waiting={inbox.waiting}
              onPreview={preview}
              onAccept={accept}
            />
          ) : (
            <Thread
              key={selectedThread.sessionId}
              thread={selectedThread}
              mine={selectedThread.mineFlag}
              onAccept={() => accept(selectedThread.sessionId)}
              onSend={(text) => send(selectedThread.sessionId, text)}
              onEnd={() => end(selectedThread.sessionId)}
              onDismiss={() => dismiss(selectedThread.sessionId)}
              onSaveCase={(fields) => saveCase(selectedThread.sessionId, fields)}
              onOpenCase={onOpenCase}
            />
          )}
        </div>
      )}

      <button onClick={() => setOpen((o) => !o)} style={S.pill} aria-expanded={open}>
        <span style={{ ...S.dot, background: available ? 'var(--accent3)' : 'var(--text3)' }} />
        <span>Chat</span>
        <span style={{ color: 'var(--text3)', fontWeight: 500 }}>{available ? 'Available' : 'Away'}</span>
        {waitingCount + totalUnread > 0 && <span style={S.badge}>{waitingCount + totalUnread}</span>}
      </button>
    </div>
  );
}

function Tab({ active, onClick, children, badge = 0, dim = false }) {
  return (
    <button onClick={onClick} style={{
      ...S.tab,
      color: active ? 'var(--text)' : dim ? 'var(--text3)' : 'var(--text2)',
      borderBottomColor: active ? 'var(--accent)' : 'transparent',
    }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 }}>{children}</span>
      {badge > 0 && <span style={{ ...S.badge, marginLeft: 6 }}>{badge}</span>}
    </button>
  );
}

function Queue({ available, waiting, onPreview, onAccept }) {
  if (!available) {
    return <div style={S.empty}>You're Away. Flip to <b>Available for chat</b> and shoppers who ask the assistant for a person will land here.</div>;
  }
  if (waiting.length === 0) {
    return <div style={S.empty}>Nobody waiting. You'll hear a ping when a shopper asks for a person — anyone not picked up in 3 minutes is offered a callback.</div>;
  }
  return (
    <div style={S.scroll}>
      {waiting.map((w) => (
        <div key={w.sessionId} style={S.queueRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <b style={{ fontSize: 13 }}>{w.customerLabel}</b>
            {w.customerSignedIn && <span style={S.chip}>signed in</span>}
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 11, color: (w.waitingMs || 0) > 90_000 ? 'var(--accent2)' : 'var(--text3)' }}>
              {formatWait(w.waitingMs)}
            </span>
          </div>
          <div style={S.sub}>{[w.page, w.locale].filter(Boolean).join(' · ')}</div>
          {w.preview && <div style={S.previewText}>“{w.preview}”</div>}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button onClick={() => onAccept(w.sessionId)} style={S.primary}>Accept</button>
            <button onClick={() => onPreview(w.sessionId)} style={S.secondary}>Read first</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Thread({ thread, mine, onAccept, onSend, onEnd, onDismiss, onSaveCase, onOpenCase }) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  // Open by default: what the shopper already told the assistant is the first
  // thing worth reading, before or after accepting.
  const [showContext, setShowContext] = useState(true);
  const bottomRef = useRef(null);
  const live = thread.status === 'active' && mine;
  const closed = !['waiting', 'active'].includes(thread.status);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [thread.messages?.length, showContext]);

  async function submit() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await onSend(text);
      setDraft('');
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  }

  const nonEnglish = thread.locale && !/^en\b/i.test(thread.locale);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={S.threadHead}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{thread.customerLabel || 'Visitor'}</div>
          <div style={S.sub}>
            {[thread.customerEmail, thread.page].filter(Boolean).join(' · ') || (thread.customerSignedIn ? 'Signed in' : 'Guest — not signed in')}
          </div>
        </div>
        {live && (confirmEnd
          ? <button onClick={onEnd} style={{ ...S.secondary, color: 'var(--accent2)', borderColor: 'var(--accent2)' }}>Confirm end</button>
          : <button onClick={() => setConfirmEnd(true)} style={S.secondary}>End chat</button>)}
        {closed && <button onClick={onDismiss} style={S.secondary}>Close</button>}
      </div>
      {nonEnglish && (
        <div style={{ ...S.banner, background: 'var(--bg3)', color: 'var(--text2)' }}>
          Shopper's browser language is {thread.locale}. Human follow-up is English-first — keep it simple.
        </div>
      )}

      <CasePanel kase={thread.case} onSave={onSaveCase} onOpen={onOpenCase} />

      <div style={S.scroll}>
        {Array.isArray(thread.context) && thread.context.length > 0 && (
          <div style={S.context}>
            <button onClick={() => setShowContext((s) => !s)} style={S.linkBtn}>
              {showContext ? '▾' : '▸'} Assistant conversation before you joined ({thread.context.length})
            </button>
            {showContext && thread.context.map((c, i) => (
              <div key={i} style={{ ...S.ctxLine, color: c.role === 'customer' ? 'var(--text)' : 'var(--text2)' }}>
                <b>{c.role === 'customer' ? 'Shopper' : 'Assistant'}:</b> {c.text}
              </div>
            ))}
            {showContext && <div style={{ ...S.sub, marginTop: 4 }}>As sent by the shopper's browser.</div>}
          </div>
        )}
        {(thread.messages || []).map((m) => (
          m.from === 'system'
            ? <div key={m.seq} style={S.system}>{systemLine(m)}</div>
            : (
              <div key={m.seq} style={{ display: 'flex', justifyContent: m.from === 'agent' ? 'flex-end' : 'flex-start' }}>
                <div style={m.from === 'agent' ? S.bubbleMe : S.bubbleThem}>{m.text}</div>
              </div>
            )
        ))}
        {closed && <div style={S.system}>{endedLine(thread.endedBy)}</div>}
        <div ref={bottomRef} />
      </div>

      {thread.status === 'waiting' && (
        <div style={S.composer}>
          <button onClick={onAccept} style={{ ...S.primary, width: '100%' }}>Accept this chat</button>
        </div>
      )}
      {thread.status === 'active' && !mine && (
        <div style={{ ...S.composer, ...S.sub }}>{thread.agentName} has this chat.</div>
      )}
      {live && (
        <div style={S.composer}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder="Reply to the shopper — Enter to send, Shift+Enter for a new line"
            rows={2}
            maxLength={2000}
            style={S.input}
            autoFocus
          />
          <button onClick={submit} disabled={sending || !draft.trim()} style={{ ...S.primary, alignSelf: 'stretch' }}>Send</button>
        </div>
      )}
    </div>
  );
}

// The Case this chat opened, drafted from the assistant conversation. The
// staffer is the editor: category, subject and priority are theirs to fix,
// and the AI summary is shown as the draft it is. Local form state re-syncs
// from the server only while untouched, so a 2s poll never wipes an edit in
// progress.
function CasePanel({ kase, onSave, onOpen }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const dirty = !!form && Object.keys(caseEdits(kase, form)).length > 0;

  useEffect(() => {
    if (kase && !dirty) setForm({ subject: kase.subject || '', category: kase.category || 'General', priority: kase.priority || 'High' });
  }, [kase?.subject, kase?.category, kase?.priority]);

  if (!kase) {
    return <div style={{ ...S.caseBar, color: 'var(--text3)' }}>Opening a case for this chat…</div>;
  }

  async function save() {
    const fields = caseEdits(kase, form);
    if (!Object.keys(fields).length) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(fields);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.caseBar}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button onClick={() => setOpen((o) => !o)} style={S.linkBtn} aria-expanded={open}>
          {open ? '▾' : '▸'} Case {kase.number ? `#${kase.number}` : '(number pending)'}
        </button>
        {kase.curated && <span style={S.chip} title="Drafted by AI from the chat — check it">AI draft</span>}
        {kase.priority && <span style={{ ...S.chip, color: kase.priority === 'High' ? 'var(--accent2)' : 'var(--text2)' }}>{kase.priority}</span>}
        {onOpen && kase.id && (
          <button onClick={() => onOpen(kase.id)} style={{ ...S.linkBtn, marginLeft: 'auto' }} title="Open in Tickets">Open ↗</button>
        )}
      </div>
      {!open && (
        <div style={{ ...S.sub, marginTop: 3 }} title={kase.subject || ''}>
          [{kase.category || 'General'}] {kase.subject || '—'}
        </div>
      )}
      {open && form && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={S.select} aria-label="Category">
              {CASE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} style={S.select} aria-label="Priority">
              {CASE_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <input
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            maxLength={110}
            placeholder="What this case is about"
            aria-label="Subject"
            style={{ ...S.input, flex: 'none' }}
          />
          <div style={{ fontSize: 10.5, color: 'var(--text3)' }}>The customer can see the subject on their dashboard.</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={save} disabled={!dirty || saving} style={{ ...S.primary, opacity: !dirty || saving ? 0.5 : 1 }}>
              {saving ? 'Saving…' : 'Save case'}
            </button>
            {dirty && !saving && (
              <button onClick={() => setForm({ subject: kase.subject || '', category: kase.category || 'General', priority: kase.priority || 'High' })} style={S.secondary}>Undo</button>
            )}
            {error && <span style={{ fontSize: 11, color: 'var(--accent2)' }}>{error}</span>}
          </div>
          {kase.summary && (
            <div>
              <button onClick={() => setShowSummary((v) => !v)} style={S.linkBtn}>{showSummary ? '▾' : '▸'} AI summary (draft)</button>
              {showSummary && <div style={{ ...S.ctxLine, color: 'var(--text2)' }}>{kase.summary}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// One short two-tone chime. WebAudio so there is no asset to ship, and a
// failure (autoplay policy before any click) is silent rather than an error.
function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [[880, 0], [1320, 0.12]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + 0.2);
    });
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch { /* sound is a nicety */ }
}

const S = {
  root: { position: 'fixed', right: 20, bottom: 20, zIndex: 900, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 },
  pill: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
    background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 999,
    color: 'var(--text)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
  },
  panel: {
    width: 'min(380px, calc(100vw - 24px))', height: 540, maxHeight: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column',
    background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
    boxShadow: '0 16px 48px rgba(0,0,0,0.5)', overflow: 'hidden',
  },
  header: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border)' },
  toggle: {
    display: 'flex', alignItems: 'center', gap: 7, padding: '5px 10px', background: 'var(--bg3)',
    border: '1px solid var(--border)', borderRadius: 999, color: 'var(--text)', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },
  meta: { fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' },
  iconBtn: { background: 'none', border: 'none', color: 'var(--text3)', fontSize: 16, cursor: 'pointer', padding: '0 4px' },
  dot: { width: 8, height: 8, borderRadius: 4, display: 'inline-block', flexShrink: 0 },
  tabs: { display: 'flex', gap: 2, padding: '0 8px', borderBottom: '1px solid var(--border)', overflowX: 'auto' },
  tab: {
    display: 'flex', alignItems: 'center', background: 'none', border: 'none', borderBottom: '2px solid transparent',
    padding: '8px 8px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  badge: {
    minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: 'var(--accent2)', color: '#fff',
    fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center',
  },
  banner: { padding: '6px 12px', fontSize: 12, background: 'rgba(250,109,138,0.12)', color: 'var(--accent2)', borderBottom: '1px solid var(--border)' },
  empty: { padding: 20, fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 },
  scroll: { flex: 1, minHeight: 0, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 6 },
  queueRow: { padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg3)' },
  chip: { fontSize: 10, padding: '1px 6px', borderRadius: 6, background: 'var(--bg2)', color: 'var(--accent3)', border: '1px solid var(--border)' },
  sub: { fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  previewText: { fontSize: 12, color: 'var(--text2)', marginTop: 4, lineHeight: 1.4 },
  primary: {
    padding: '6px 12px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 7,
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },
  secondary: {
    padding: '5px 10px', background: 'none', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 7,
    fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  },
  caseBar: { padding: '7px 12px', borderBottom: '1px solid var(--border)', background: 'var(--bg3)', fontSize: 12 },
  select: {
    background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6,
    padding: '4px 6px', fontSize: 12, fontFamily: 'inherit',
  },
  threadHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', justifyContent: 'space-between' },
  context: { padding: 8, borderRadius: 8, background: 'var(--bg3)', border: '1px dashed var(--border)', marginBottom: 4 },
  ctxLine: { fontSize: 12, lineHeight: 1.45, marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--text2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 },
  system: { alignSelf: 'center', fontSize: 11, color: 'var(--text3)', padding: '2px 0', textAlign: 'center' },
  bubbleMe: {
    maxWidth: '80%', padding: '7px 10px', borderRadius: '10px 10px 2px 10px', background: 'var(--accent)', color: '#fff',
    fontSize: 13, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  },
  bubbleThem: {
    maxWidth: '80%', padding: '7px 10px', borderRadius: '10px 10px 10px 2px', background: 'var(--bg3)', color: 'var(--text)',
    fontSize: 13, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word', border: '1px solid var(--border)',
  },
  composer: { display: 'flex', gap: 6, padding: 10, borderTop: '1px solid var(--border)' },
  input: {
    flex: 1, resize: 'none', background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border)',
    borderRadius: 7, padding: '7px 9px', fontSize: 13, fontFamily: 'inherit', outline: 'none',
  },
};
